/**
 * Pipeline board view-projection helpers.
 *
 * The board's server/manual order lives in the query cache; non-manual sort
 * modes are *pure projections* over a column's cards — they never mutate the
 * cache, so optimistic drag updates stay correct.
 */

import type { PipelineBoardCard } from "../types";
import { daysSince } from "./formatRelative";
import { prioOf } from "./tags";

export type BoardSortMode = "manual" | "prio" | "stale";

/** Days the card has dwelt in its current stage (null when unknown). */
export function dwellDays(
  card: PipelineBoardCard,
  now: Date = new Date(),
): number | null {
  return daysSince(card.stage_entered_at, now);
}

/** A card is stale when it's dwelt ≥ STALE_DWELL_DAYS with no interactions since entry.
 *  Python twin: system/services/pipelines.py :: STALE_DWELL_DAYS / dwell_days / is_stale
 *  (the attention finder) — change both sides together. */
export const STALE_DWELL_DAYS = 14;

export function isStale(
  card: PipelineBoardCard,
  now: Date = new Date(),
): boolean {
  // A parked card is not being worked on purpose – never stale.
  if (card.status === "parked") return false;
  const dwell = dwellDays(card, now);
  if (dwell == null) return false;
  return dwell >= STALE_DWELL_DAYS && (card.interactions_since_stage_entry ?? 0) === 0;
}

/**
 * Ascending priority (P0 first), cards without a prio tag last. Relies on a
 * stable Array.sort so the server/manual order is the tiebreak within a level.
 */
export function sortByPrio(cards: PipelineBoardCard[]): PipelineBoardCard[] {
  return [...cards].sort((a, b) => {
    const pa = prioOf(a.tags);
    const pb = prioOf(b.tags);
    if (pa == null && pb == null) return 0;
    if (pa == null) return 1;
    if (pb == null) return -1;
    return pa - pb;
  });
}

/**
 * The neglect view: fewest interactions-since-entry first, longest dwell as
 * the tiebreak (so the most-stalled cards bubble to the top).
 */
export function sortByStale(cards: PipelineBoardCard[]): PipelineBoardCard[] {
  return [...cards].sort((a, b) => {
    const ia = a.interactions_since_stage_entry ?? 0;
    const ib = b.interactions_since_stage_entry ?? 0;
    if (ia !== ib) return ia - ib;
    const da = dwellDays(a) ?? 0;
    const db = dwellDays(b) ?? 0;
    return db - da;
  });
}
